/**
 * Client List Page - Modern Design
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Filter, Plus, Edit, Users, ChevronLeft, ChevronRight } from 'lucide-react'
import { useClients } from '@/hooks'
import { toast } from '@/lib/toast'
import { calculateAge } from '@/lib/utils'

export function ClientList() {
  // State for filters and pagination
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [gender, setGender] = useState<string>('')

  // Fetch clients with filters
  const { data, isLoading, error, refetch } = useClients(
    { search, gender: gender || undefined },
    { page, limit }
  )

  // Handle search submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput.trim())
    setPage(1)
  }

  // Handle filter change
  const handleFilterChange = (value: string) => {
    setGender(value)
    setPage(1)
  }

  // Format full name
  const formatName = (firstName?: string | null, lastName?: string | null) => {
    const parts = [firstName, lastName].filter(Boolean)
    return parts.join(' ') || 'N/A'
  }

  // Format address
  const formatAddress = (
    street?: string | null,
    barangay?: string | null,
    city?: string | null,
    postal?: string | null
  ) => {
    const parts = []
    if (street) parts.push(street)
    if (barangay) parts.push(`Brgy. ${barangay}`)
    if (city) parts.push(city)
    if (postal) parts.push(postal)
    return parts.join(', ') || 'N/A'
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Clients</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Users className="h-8 w-8" />
            Clients
          </h1>
          <p className="text-gray-600 mt-1">
            Manage and view all client records
          </p>
        </div>
        <Link to="/clients/new">
          <button className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm font-medium">
            <Plus className="w-5 h-5" />
            New Client
          </button>
        </Link>
      </div>

      {/* Search and Filters Card */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <form onSubmit={handleSearch}>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by client name..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Gender Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
              <select
                value={gender}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="appearance-none pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer bg-white min-w-[200px]"
              >
                <option value="">All Genders</option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
                <option value="Others">Others</option>
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 rotate-90 pointer-events-none" />
            </div>

            {/* Search Button */}
            <button
              type="submit"
              className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              Search
            </button>

            {/* Clear Button */}
            {(search || gender) && (
              <button
                type="button"
                onClick={() => {
                  setSearch('')
                  setSearchInput('')
                  setGender('')
                  setPage(1)
                }}
                className="border border-gray-300 hover:bg-gray-50 px-6 py-3 rounded-lg transition-colors font-medium whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {/* Active Filters */}
        {(search || gender) && (
          <div className="mt-4 pt-4 border-t flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Active filters:</span>
            {search && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                Search: "{search}"
              </span>
            )}
            {gender && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                Gender: {gender}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results Summary */}
      {data && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{data.items.length}</span> of{' '}
            <span className="font-semibold text-gray-900">{data.total}</span> clients
          </p>
          {data.pages > 1 && (
            <p className="text-gray-600">
              Page <span className="font-semibold text-gray-900">{page}</span> of{' '}
              <span className="font-semibold text-gray-900">{data.pages}</span>
            </p>
          )}
        </div>
      )}

      {/* Table Card */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
            <p className="text-gray-600">Loading clients...</p>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No clients found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
            <Link to="/clients/new">
              <button className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg transition-colors">
                Add First Client
              </button>
            </Link>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Age
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Gender
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.items.map((client) => {
                    const age = calculateAge(client.birthday)
                    const address = formatAddress(
                      client.street_name,
                      client.barangay,
                      client.city,
                      client.postal_code
                    )

                    return (
                      <tr
                        key={client.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">
                            {formatName(client.firstname, client.lastname)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-700">
                          {age !== null ? `${age} years` : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            client.gender === 'Male' ? 'bg-blue-100 text-blue-800' :
                            client.gender === 'Female' ? 'bg-pink-100 text-pink-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {client.gender || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-700 max-w-md truncate">
                          {address}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Link to={`/clients/${client.id}/edit?page=${page}`}>
                            <button
                              className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors"
                              title="Edit Client"
                            >
                              <Edit className="w-4 h-4" />
                              <span className="text-sm">Edit</span>
                            </button>
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.pages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {page} of {data.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1 || isLoading}
                    className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(data.pages, p + 1))}
                    disabled={page === data.pages || isLoading}
                    className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ClientList
